/**
 * Customer "Repeat weekly" panel for the daycare branch of the /book wizard
 * (migration 0096).
 *
 * Self-contained: the wizard mounts it with the chosen service + pets +
 * facility and nothing else. Collapsed by default; when enabled it owns the
 * weekday / date / session inputs, calls
 * `POST /api/customer/recurring-series/preview` for a live preview of which
 * dates will be booked vs skipped, and `POST /api/customer/recurring-series`
 * to create the series, then routes to /account/recurring-visits.
 */
import {
  SESSION_OPTIONS,
  type SeriesCreateResult,
  type SeriesPreview,
  type SessionType,
  WEEKDAY_OPTIONS,
  describeSkipReason,
  formatDay,
} from '@/lib/recurrence';
import { AlertTriangle, Loader2, Repeat } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomerApiError, customerApi } from '../../customer_api';

interface Props {
  serviceId: string;
  petIds: string[];
  facilityId: string;
  defaultSessionType?: SessionType | undefined;
}

function messageFor(err: unknown): string {
  if (err instanceof CustomerApiError) {
    const body = (err.body ?? {}) as { error?: string; issues?: string[] };
    if (Array.isArray(body.issues) && body.issues[0]) return body.issues[0];
    switch (body.error) {
      case 'vaccine_required':
        return 'A vaccination record is missing or expired. Add it under Pets and try again.';
      case 'booking_not_configured':
        return 'Recurring daycare is not available yet — please contact us.';
      case 'pet_not_found':
        return 'One of the selected pets could not be found.';
      default:
        return 'Could not save the weekly booking. Please try again.';
    }
  }
  return 'Could not save the weekly booking. Please try again.';
}

export function RecurringDaycarePanel({
  serviceId,
  petIds,
  facilityId,
  defaultSessionType,
}: Props) {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(false);
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sessionType, setSessionType] = useState<SessionType>(defaultSessionType ?? 'full_day');
  const [preview, setPreview] = useState<SeriesPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const ready = enabled && weekdays.length > 0 && Boolean(startDate) && petIds.length > 0;

  const body = () => ({
    petIds,
    serviceId,
    ...(facilityId ? { facilityId } : {}),
    sessionType,
    weekdays,
    startDate,
    endDate: endDate || null,
  });

  useEffect(() => {
    if (!ready) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setError(null);
    const t = setTimeout(() => {
      customerApi<SeriesPreview>('/api/customer/recurring-series/preview', {
        method: 'POST',
        body: JSON.stringify(body()),
      })
        .then((p) => {
          if (!cancelled) setPreview(p);
        })
        .catch((e) => {
          if (!cancelled) {
            setPreview(null);
            setError(messageFor(e));
          }
        })
        .finally(() => {
          if (!cancelled) setPreviewLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, weekdays.join(','), startDate, endDate, sessionType, petIds.join(','), facilityId]);

  const toggleWeekday = (d: number) =>
    setWeekdays((w) => (w.includes(d) ? w.filter((x) => x !== d) : [...w, d].sort()));

  const submit = async () => {
    if (!ready) return;
    setSubmitting(true);
    setError(null);
    try {
      await customerApi<SeriesCreateResult>('/api/customer/recurring-series', {
        method: 'POST',
        body: JSON.stringify(body()),
      });
      navigate('/account/recurring-visits', { replace: true, state: { created: true } });
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setSubmitting(false);
    }
  };

  const skippedByDate = new Map((preview?.skipped ?? []).map((s) => [s.date, s.reason]));

  return (
    <section className="mt-6 rounded-md border border-border">
      <label className="flex cursor-pointer items-center gap-3 p-3">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="size-4"
        />
        <Repeat size={16} aria-hidden />
        <span className="text-sm font-medium">Repeat weekly instead</span>
      </label>
      <p className="px-3 pb-3 text-xs text-muted-foreground">
        Book the same days every week. We keep the next 12 weeks booked for you and add more
        automatically — you can stop at any time from your account.
      </p>

      {enabled && (
        <div className="space-y-4 border-t border-border p-3">
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Days of the week
            </p>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Days of the week">
              {WEEKDAY_OPTIONS.map((w) => {
                const on = weekdays.includes(w.value);
                return (
                  <button
                    key={w.value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleWeekday(w.value)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                      on
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-surface text-muted-foreground'
                    }`}
                  >
                    {w.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="cust-rec-start" className="mb-1 block text-xs text-muted-foreground">
                First visit
              </label>
              <input
                id="cust-rec-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="cust-rec-end" className="mb-1 block text-xs text-muted-foreground">
                Until (optional)
              </label>
              <input
                id="cust-rec-end"
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Session
            </p>
            <div className="grid gap-2">
              {SESSION_OPTIONS.map((o) => (
                <label
                  key={o.value}
                  className={`flex cursor-pointer items-center gap-3 rounded-md border p-2 text-sm ${
                    sessionType === o.value ? 'border-primary' : 'border-border'
                  }`}
                >
                  <input
                    type="radio"
                    name="cust-rec-session"
                    checked={sessionType === o.value}
                    onChange={() => setSessionType(o.value)}
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </div>

          {ready && previewLoading && !preview && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground" aria-busy="true">
              <Loader2 size={16} className="animate-spin" />
              Checking availability…
            </p>
          )}
          {preview && (
            <div>
              <p className="mb-2 text-xs text-muted-foreground">
                Next {preview.horizonWeeks} weeks: <strong>{preview.ok.length}</strong> visit
                {preview.ok.length === 1 ? '' : 's'} available
                {preview.skipped.length > 0 && (
                  <>
                    , <strong>{preview.skipped.length}</strong> unavailable
                  </>
                )}
              </p>
              {preview.dates.length === 0 ? (
                <p className="text-sm text-muted-foreground">No dates match that rule.</p>
              ) : (
                <ul className="grid gap-1 sm:grid-cols-2">
                  {preview.dates.map((d) => {
                    const reason = skippedByDate.get(d);
                    return (
                      <li
                        key={d}
                        className={`flex items-center justify-between rounded-md border px-3 py-1.5 text-sm ${
                          reason ? 'border-border text-muted-foreground' : 'border-border'
                        }`}
                      >
                        <span>{formatDay(d)}</span>
                        <span className="text-xs">
                          {reason ? describeSkipReason(reason) : 'Available'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {error && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <AlertTriangle size={16} />
              {error}
            </p>
          )}

          <button
            type="button"
            disabled={!ready || submitting || previewLoading || (preview?.ok.length ?? 0) === 0}
            onClick={submit}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            Book weekly visits
          </button>
          <p className="text-xs text-muted-foreground">
            Visits use your daycare pack or membership where you have one; otherwise each visit is
            charged on the day.
          </p>
        </div>
      )}
    </section>
  );
}
